import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { useToast } from '../ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Users,
  BarChart3,
  FileSpreadsheet,
  Settings,
  LogOut,
  RefreshCw,
  Loader2,
  Shield,
  Activity,
  Database,
  Monitor,
  Zap,
  Clock,
  Briefcase
} from 'lucide-react';

// Import tab components
import { DashboardOverviewTab } from './tabs/DashboardOverviewTab';
import { UserManagementTab } from './tabs/UserManagementTab';
import { ProjectsLocationsTab } from './tabs/ProjectsLocationsTab';
import { ReportsAnalyticsTab } from './tabs/ReportsAnalyticsTab';
import { SystemSettingsTab } from './tabs/SystemSettingsTab';
import { OvertimeTab } from './tabs/OvertimeTab';
import { ActivitiesReportTab } from './tabs/ActivitiesReportTab';
import { HRMonthlyReportTab } from './tabs/HRMonthlyReportTab';

// Import contexts
import { useRealTime } from '../../contexts/RealTimeContext';
import { useDelayedSignalR } from '../../hooks/useDelayedSignalR';
import { useAdminData } from '../../hooks/useAdminData';

interface RefactoredAdminDashboardProps {
  currentUser: User;
  onLogout: () => void;
}

export const RefactoredAdminDashboard: React.FC<RefactoredAdminDashboardProps> = ({
  currentUser,
  onLogout
}) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const { toast } = useToast();
  const realTime = useRealTime();
  
  // Use delayed SignalR connection for admin role
  const { 
    isConnecting: signalRConnecting, 
    connectionError: signalRError,
    connectionState: signalRState 
  } = useDelayedSignalR(currentUser.id.toString(), currentUser.role, 1500);

  // Use admin data hook for centralized data management
  const { 
    data: { workEntries, allUsers, teams, projects, locations }, 
    isLoading: dataLoading, 
    error, 
    refreshData,
    updateWorkEntry, 
    removeWorkEntry, 
    updateUser, 
    removeUser,
    addProject,
    updateProject,
    removeProject,
    addLocation,
    updateLocation,
    removeLocation
  } = useAdminData(selectedDate);

  // Initialize real-time connection - handled by useDelayedSignalR hook
  useEffect(() => {
    // Log connection state changes
    console.log('AdminDashboard: SignalR connection state:', signalRState, {
      isConnecting: signalRConnecting,
      connectionError: signalRError?.message || 'none',
      realTimeState: realTime.state
    });
    
    // Show error toast if connection fails
    if (signalRError) {
      toast({
        title: "Connection Notice",
        description: "Real-time updates may be unavailable. The dashboard will continue to work normally.",
        variant: "destructive"
      });
    }
  }, [signalRState, signalRError, realTime.state, signalRConnecting, toast]);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await refreshData();
      toast({
        title: 'Actualizat',
        description: 'Datele au fost actualizate cu succes',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut actualiza datele',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate metrics for header
  const activeWorkers = workEntries.filter(e => e.isActive && !e.activeBreak).length;
  const onBreak = workEntries.filter(e => e.isActive && e.activeBreak).length;
  const totalUsers = allUsers.length;
  const totalTeams = teams.length;

  // Show loading overlay while connecting to SignalR
  if (realTime.state.connectionState === 'Connecting' || 
      (realTime.state.connectionState === 'Disconnected' && !realTime.state.error)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            <h2 className="text-xl font-semibold">Conectare la server...</h2>
            <p className="text-sm text-gray-600">Așteptați stabilirea conexiunii</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard Administrator
              </h1>
              <Badge variant="secondary" className="text-sm">
                <Shield className="h-3 w-3 mr-1" />
                {currentUser.name}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Connection Status */}
              <div className="flex items-center gap-2 mr-4">
                <div className={`w-2 h-2 rounded-full ${
                  realTime.state.isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`} />
                <span className="text-sm text-gray-600">
                  {realTime.state.isConnected ? 'Conectat' : 'Deconectat'}
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading || dataLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${(isLoading || dataLoading) ? 'animate-spin' : ''}`} />
                Actualizează
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="h-4 w-4" />
                Deconectare
              </Button>
            </div>
          </div>

          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="flex items-center gap-3 bg-green-50 p-3 rounded-lg">
              <Activity className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Activi</p>
                <p className="font-semibold">{activeWorkers}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-orange-50 p-3 rounded-lg">
              <Zap className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">În Pauză</p>
                <p className="font-semibold">{onBreak}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Total Utilizatori</p>
                <p className="font-semibold">{totalUsers}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-purple-50 p-3 rounded-lg">
              <Database className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">Echipe</p>
                <p className="font-semibold">{totalTeams}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-8 w-full max-w-7xl mx-auto">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Dash</span>
            </TabsTrigger>

            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Utilizatori</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>

            <TabsTrigger value="overtime" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Overtime</span>
              <span className="sm:hidden">OT</span>
            </TabsTrigger>

            <TabsTrigger value="hrreport" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">Raport HR</span>
              <span className="sm:hidden">HR</span>
            </TabsTrigger>

            <TabsTrigger value="projects" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">Proiecte</span>
              <span className="sm:hidden">Proj</span>
            </TabsTrigger>

            <TabsTrigger value="reports" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Rapoarte</span>
              <span className="sm:hidden">Rep</span>
            </TabsTrigger>

            <TabsTrigger value="activities" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Activități</span>
              <span className="sm:hidden">Act</span>
            </TabsTrigger>

            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Setări</span>
              <span className="sm:hidden">Set</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Overview Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <DashboardOverviewTab
              workEntries={workEntries}
              allUsers={allUsers}
              teams={teams}
              projects={projects}
              locations={locations}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              isLoading={dataLoading}
              onRefresh={refreshData}
            />
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <UserManagementTab
              allUsers={allUsers}
              teams={teams}
              projects={projects}
              locations={locations}
              onUpdateUser={updateUser}
              onRemoveUser={removeUser}
              onRefresh={refreshData}
              isLoading={dataLoading}
            />
          </TabsContent>
          
          {/* Overtime Tab */}
          <TabsContent value="overtime" className="space-y-4">
            <OvertimeTab
              workEntries={workEntries}
              allUsers={allUsers}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              isLoading={dataLoading}
            />
          </TabsContent>

          {/* HR Monthly Report Tab */}
          <TabsContent value="hrreport" className="space-y-4">
            <HRMonthlyReportTab
              isLoading={dataLoading}
            />
          </TabsContent>

          {/* Projects & Locations Tab */}
          <TabsContent value="projects" className="space-y-4">
            <ProjectsLocationsTab
              projects={projects}
              locations={locations}
              onRefresh={refreshData}
              isLoading={dataLoading}
              onAddProject={addProject}
              onUpdateProject={updateProject}
              onRemoveProject={removeProject}
              onAddLocation={addLocation}
              onUpdateLocation={updateLocation}
              onRemoveLocation={removeLocation}
            />
          </TabsContent>

          {/* Reports & Analytics Tab */}
          <TabsContent value="reports" className="space-y-4">
            <ReportsAnalyticsTab
              workEntries={workEntries}
              allUsers={allUsers}
              teams={teams}
              projects={projects}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              isLoading={dataLoading}
            />
          </TabsContent>

          {/* Activities Report Tab */}
          <TabsContent value="activities" className="space-y-4">
            <ActivitiesReportTab
              allUsers={allUsers}
              teams={teams}
              projects={projects}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              isLoading={dataLoading}
            />
          </TabsContent>

          {/* System Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <SystemSettingsTab
              teams={teams}
              allUsers={allUsers}
              onRefresh={refreshData}
              isLoading={dataLoading}
              currentUser={currentUser}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};